import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { Pill } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can, canEditOwnDraft } from '../../lib/permissions';
import { dateLabel, money, quotationStatus, customerShort } from '../../lib/format';
import { QuotationEditor } from './QuotationEditor';
import { QuotationDetail } from './QuotationDetail';
import type { Quotation } from '../../lib/types';

export function QuotationsPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canWrite = can(user, 'quotations', 'create');
  const [reloadSignal, setReloadSignal] = useState(0);
  // mode: 关闭 / 新建 / 编辑（携带待编辑报价单）
  const [editor, setEditor] = useState<{ mode: 'new' } | { mode: 'edit'; q: Quotation } | null>(null);
  // 行级「编辑」正在拉取全量单据的报价单 id（列表行是投影、不含明细行，必须先取详情再开编辑器）
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);

  const refresh = () => setReloadSignal((n) => n + 1);

  // 行级编辑入口：拉全量（含 lines）→ 开模块编辑器。失败不开编辑器——
  // 用投影行开编辑器会得到一条空明细，用户若保存会用空行覆盖真实明细。
  const openRowEdit = async (row: Quotation) => {
    setEditLoadingId(row.id);
    try {
      const full = await api.detail<Quotation>(`/api/quotations/${row.id}`);
      setEditor({ mode: 'edit', q: full });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      window.alert('读取报价单明细失败，请稍后重试或在列表展开该行后点「编辑草稿」');
    } finally {
      setEditLoadingId(null);
    }
  };

  const columns: Column<Quotation>[] = [
    { header: '报价单号', render: (q) => (
      <span className="num strong">{q.number}</span>
    ) },
    // 客户名必须限宽截断（2026-10-29）：西语公司名很长，
    // 「PETROCICAL SOCIEDAD POR ACCIONES SIMPLIFICADA」会把整表撑宽，
    // 溢出后右侧「状态」列被 sticky 操作列遮住，只露出半个 Pill。
    // 截断 + title 悬停看全名，是列表里唯一可行的处理。
    { header: '客户', render: (q) => <span title={q.customer_name || ''}>{customerShort(q.customer_name)}</span> },
    { header: '报价日期', render: (q) => <span className="num">{dateLabel(q.quotation_date)}</span> },
    { header: '有效期至', render: (q) => <span className="num muted">{dateLabel(q.valid_until)}</span> },
    { header: '含税总额', align: 'right', render: (q) => <span className="num strong">{money(q.grand_total, q.currency)}</span> },
    { header: '状态', render: (q) => { const s = quotationStatus(q.status); return <Pill tone={s.tone} label={s.label} />; } },
    { header: '版本', align: 'right', render: (q) => <span className="muted num">{q.version && q.version > 1 ? `v${q.version}` : '—'}</span> },
  ];

  return (
    <>
      {editor ? (
        <QuotationEditor
          initial={editor.mode === 'edit' ? editor.q : null}
          onCancel={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            refresh();
            // 让被保存的单号进入列表搜索，方便定位结果。
            const url = new URL(window.location.href);
            url.searchParams.set('q', saved.number);
            window.history.replaceState({}, '', url.toString());
          }}
        />
      ) : null}

      <ListPage<Quotation>
        title="报价单"
        desc="报价单 · /api/quotations，支持新建/编辑草稿、复制、版本修订、状态流转、转合同、西语打印与审计留痕（当页展开，不用抽屉）"
        endpoint="/api/quotations"
        columns={columns}
        rowId={(q) => q.id}
        searchPlaceholder="搜索报价单号 / 客户 / 状态 / 备注…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => (
          <>
            {canWrite && row.status === 'draft' && canEditOwnDraft(user, 'quotations', row) ? (
              <button
                className="btn link"
                disabled={editLoadingId === row.id}
                title={`编辑报价单 ${row.number}（表头 + 明细行均可改）`}
                onClick={(e) => { e.stopPropagation(); void openRowEdit(row); }}
              >
                {editLoadingId === row.id ? '载入中…' : '编辑'}
              </button>
            ) : null}
            <RecordActions type="quotations" row={row} fields={RECORD_FIELDS['quotations']} label={String(row.number)} hideEdit onDone={() => setReloadSignal((n) => n + 1)} />
          </>
        )}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setEditor((e) => (e && e.mode === 'new' ? null : { mode: 'new' }))}>
            {editor?.mode === 'new' ? '收起新建' : '新建报价单'}
          </button>
        ) : undefined}
        renderDetail={(row) => (
          <QuotationDetail
            row={row}
            onEdit={(q) => setEditor({ mode: 'edit', q })}
            onChanged={refresh}
          />
        )}
      />
      {!canWrite ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>
          当前角色为只读。报价单的新建、编辑与状态流转由销售与管理员执行（服务端强制校验）。
        </div>
      ) : null}
    </>
  );
}
